import { useState } from "react";
import { Copy, Mail, Check } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

export default function MailDialog({ mails = [] }) {
  const [active, setActive] = useState(0);
  const [copied, setCopied] = useState(false);
  const mail = mails[active];

  const copy = async () => {
    const text = `Subject: ${mail.subject}\n\n${mail.body}`;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success("Mail copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy. Select the text and copy manually.");
    }
  };

  const mailto = () =>
    `mailto:?subject=${encodeURIComponent(mail.subject)}&body=${encodeURIComponent(mail.body)}`;

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid="open-mail-drafts-button">
          <Mail className="mr-2 h-4 w-4" />
          Mail drafts
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Mail drafts</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-muted-foreground">
          Nothing is sent from the app. Copy the mail or open it in your mail client.
        </p>
        <div className="flex flex-wrap gap-2">
          {mails.map((m, i) => (
            <button
              key={m.key}
              onClick={() => setActive(i)}
              data-testid={`mail-tab-${m.key}`}
              className={`rounded-full border px-3 py-1 text-xs transition-colors ${
                i === active ? "border-primary bg-primary text-primary-foreground" : "border-border hover:bg-secondary"
              }`}
            >
              {m.label}
            </button>
          ))}
        </div>
        {mail && (
          <div className="space-y-3">
            <div>
              <div className="text-xs text-muted-foreground">Subject</div>
              <div className="rounded-md border border-border bg-secondary/40 p-2 text-sm" data-testid="mail-subject">
                {mail.subject}
              </div>
            </div>
            <Textarea
              readOnly
              value={mail.body}
              rows={16}
              className="font-mono text-xs"
              data-testid="mail-body"
            />
            <div className="flex flex-wrap gap-2">
              <Button size="sm" onClick={copy} data-testid="mail-copy-button">
                {copied ? <Check className="mr-2 h-4 w-4" /> : <Copy className="mr-2 h-4 w-4" />}
                Copy mail
              </Button>
              <Button size="sm" variant="outline" asChild data-testid="mail-mailto-button">
                <a href={mailto()}>
                  <Mail className="mr-2 h-4 w-4" />
                  Open in mail client
                </a>
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
